/**
 * MT Header JavaScript
 * Handles dropdown menus, mobile menu, and other interactive features
 */

class MTHeader {
    constructor(headerElement) {
      this.header = headerElement;
      // Track mobile menu open state to coordinate with sticky header behavior
      this.isMobileMenuOpen = false;
      this.init();
    }
  
  init() {
    this.initDropdownMenus();
    this.initMobileMenu();
    this.initCountrySelector();
    this.initCartCount();
    this.initAccountNotification();
    this.handleStickyHeader();
    this.handleHeaderPosition();
    this.initSearchBox();
    
    // Ensure mobile menu details are closed on initial load
    const mobileMenu = this.header.querySelector('#mt-mobile-menu');
    if (mobileMenu) {
      this.initMobileMenuDetails(mobileMenu);
    }
  }
  
    /**
     * Initialize dropdown menus
     */
    initDropdownMenus() {
      const dropdownItems = this.header.querySelectorAll('.mt-header__menu-item--has-dropdown');
      
      dropdownItems.forEach(item => {
        const button = item.querySelector('.mt-header__menu-link--dropdown');
        const dropdown = item.querySelector('.mt-header__dropdown');
        
        if (!button || !dropdown) return;
  
        // Desktop: hover to open
        if (window.matchMedia('(min-width: 990px)').matches) {
          item.addEventListener('mouseenter', () => {
            this.openDropdown(item, button, dropdown);
          });
  
          item.addEventListener('mouseleave', () => {
            this.closeDropdown(item, button, dropdown);
          });
        }
  
        // Mobile: click to toggle
        button.addEventListener('click', (e) => {
          if (window.matchMedia('(max-width: 989px)').matches) {
            e.preventDefault();
            const isExpanded = button.getAttribute('aria-expanded') === 'true';
            
            if (isExpanded) {
              this.closeDropdown(item, button, dropdown);
            } else {
              // Close other dropdowns first
              dropdownItems.forEach(otherItem => {
                if (otherItem !== item) {
                  const otherButton = otherItem.querySelector('.mt-header__menu-link--dropdown');
                  const otherDropdown = otherItem.querySelector('.mt-header__dropdown');
                  if (otherButton && otherDropdown) {
                    this.closeDropdown(otherItem, otherButton, otherDropdown);
                  }
                }
              });
              this.openDropdown(item, button, dropdown);
            }
          }
        });
      });
  
      // Close dropdowns when clicking outside
      document.addEventListener('click', (e) => {
        if (!this.header.contains(e.target)) {
          dropdownItems.forEach(item => {
            const button = item.querySelector('.mt-header__menu-link--dropdown');
            const dropdown = item.querySelector('.mt-header__dropdown');
            if (button && dropdown) {
              this.closeDropdown(item, button, dropdown);
            }
          });
        }
      });
    }
  
    /**
     * Open dropdown menu
     */
    openDropdown(item, button, dropdown) {
      button.setAttribute('aria-expanded', 'true');
      item.setAttribute('aria-expanded', 'true');
      dropdown.style.display = 'block';
      
      // Trigger animation
      requestAnimationFrame(() => {
        dropdown.style.opacity = '1';
        dropdown.style.visibility = 'visible';
        dropdown.style.transform = 'translateY(0)';
      });
    }
  
    /**
     * Close dropdown menu
     */
    closeDropdown(item, button, dropdown) {
      button.setAttribute('aria-expanded', 'false');
      item.setAttribute('aria-expanded', 'false');
      
      dropdown.style.opacity = '0';
      dropdown.style.visibility = 'hidden';
      dropdown.style.transform = 'translateY(-10px)';
      
      setTimeout(() => {
        if (button.getAttribute('aria-expanded') === 'false') {
          dropdown.style.display = 'none';
        }
      }, 200);
    }
  
    /**
     * Initialize mobile menu
     * Menu expands below header instead of using drawer
     */
    initMobileMenu() {
      const mobileMenuToggle = this.header.querySelector('.mt-header__mobile-menu-toggle');
      const mobileMenu = this.header.querySelector('#mt-mobile-menu');
      
      if (!mobileMenuToggle || !mobileMenu) return;

      // Ensure initial state is correct - menu should be hidden
      mobileMenu.hidden = true;
      mobileMenuToggle.setAttribute('aria-expanded', 'false');
      
      // Initialize details elements to ensure they're closed
      this.initMobileMenuDetails(mobileMenu);
      
      // Remove is="toggle-button" if it exists to prevent conflicts
      if (mobileMenuToggle.hasAttribute('is')) {
        mobileMenuToggle.removeAttribute('is');
      }

      // 锁定body滚动
      const lockBodyScroll = () => {
        const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
        // 保存当前页面滚动位置，用于关闭菜单后恢复
        this._mobileMenuOriginalScrollY = scrollY;

        document.body.style.position = 'fixed';
        document.body.style.top = `-${0}px`;
        document.body.style.width = '100%';
        document.body.setAttribute('data-scroll-lock', scrollY.toString());
      };

      // 解锁body滚动
      const unlockBodyScroll = () => {
        const stored = document.body.getAttribute('data-scroll-lock') || '0';
        const originalScrollY = typeof this._mobileMenuOriginalScrollY === 'number'
          ? this._mobileMenuOriginalScrollY
          : parseInt(stored, 10) || 0;

        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.removeAttribute('data-scroll-lock');
        // 恢复到打开菜单前的滚动位置
        window.scrollTo(0, originalScrollY);
        this._mobileMenuOriginalScrollY = undefined;
      };
      
      // 获取图标元素
      const menuIconOpen = mobileMenuToggle.querySelector('.mt-header__menu-icon--open');
      const menuIconClose = mobileMenuToggle.querySelector('.mt-header__menu-icon--close');

      // 切换图标显示
      const toggleMenuIcon = (isOpen) => {
        if (menuIconOpen && menuIconClose) {
          if (isOpen) {
            menuIconOpen.hidden = true;
            menuIconClose.hidden = false;
          } else {
            menuIconOpen.hidden = false;
            menuIconClose.hidden = true;
          }
        }
      };

      // Add click handler to toggle menu
      mobileMenuToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const isExpanded = mobileMenuToggle.getAttribute('aria-expanded') === 'true';
        const newState = !isExpanded;
        
        // Update button state
        mobileMenuToggle.setAttribute('aria-expanded', newState ? 'true' : 'false');
        
        // Toggle icon
        toggleMenuIcon(newState);
        
        // Toggle menu visibility
        if (newState) {
          // Mark mobile menu as open to coordinate with sticky header
          this.isMobileMenuOpen = true;
          this.header.dataset.mobileMenuOpen = 'true';
          // Ensure header is visible when menu opens (avoid being translated out of view)
          this.header.classList.remove('hidden');

          // 无论当前滚动到哪里，打开菜单时都把页面滚动到顶部，这样始终看到完整的 mt-header__container
          window.scrollTo(0, 0);

          mobileMenu.hidden = false;
          mobileMenu.style.display = 'block';
          // 锁定页面滚动
          lockBodyScroll();
          // Initialize details toggle buttons after menu is visible
          this.initMobileMenuDetailsButtons(mobileMenu);
          // Initialize mobile search
          this.initMobileSearch(mobileMenu);
        } else {
          // Mark mobile menu as closed
          this.isMobileMenuOpen = false;
          this.header.dataset.mobileMenuOpen = 'false';

          mobileMenu.hidden = true;
          mobileMenu.style.display = 'none';
          // 解锁页面滚动
          unlockBodyScroll();
        }
      });
    }
    
    /**
     * Initialize details elements to ensure they're closed by default
     */
    initMobileMenuDetails(menu) {
      const detailsElements = menu.querySelectorAll('details.mt-mobile-menu__details');
      detailsElements.forEach(details => {
        // Ensure closed by default
        details.removeAttribute('open');
      });
    }
    
    /**
     * Initialize toggle buttons for details elements
     */
    initMobileMenuDetailsButtons(menu) {
      requestAnimationFrame(() => {
        const detailsElements = menu.querySelectorAll('details.mt-mobile-menu__details');
        
        detailsElements.forEach(details => {
          const toggleButton = details.querySelector('.mt-mobile-menu__dropdown-toggle');
          const summary = details.querySelector('summary');
          const link = summary?.querySelector('.mt-mobile-menu__link');
          const icon = toggleButton?.querySelector('svg');
          
          if (!toggleButton || !summary) return;
          
          // Prevent summary from toggling details when clicking the link
          if (link) {
            link.addEventListener('click', (e) => {
              // Allow link to work normally, but prevent details toggle
              e.stopPropagation();
            });
          }
          
          // Handle button click to toggle details
          toggleButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            details.toggleAttribute('open');
          });
          
          // Update icon based on details open state
          const updateIcon = () => {
            const isOpen = details.hasAttribute('open');
            toggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            
            if (icon) {
              if (isOpen) {
                icon.style.setProperty('transform', 'rotate(180deg)', 'important');
                icon.classList.add('mt-icon-rotated');
              } else {
                icon.style.setProperty('transform', 'rotate(0deg)', 'important');
                icon.classList.remove('mt-icon-rotated');
              }
            }
          };
          
          // Initial state
          updateIcon();
          
          // Watch for changes to details open attribute
          const observer = new MutationObserver(() => {
            updateIcon();
          });
          
          observer.observe(details, {
            attributes: true,
            attributeFilter: ['open']
          });
        });
      });
    }
  
    /**
     * Initialize country selector
     * Use fixed positioning to ensure popover is above all content
     */
    initCountrySelector() {
      const popover = this.header.querySelector('#mt-header-localization-form-currency');
      const button = this.header.querySelector('[aria-controls="mt-header-localization-form-currency"]');
      
      if (!popover || !button) return;
      
      // Function to update popover position
      const updatePopoverPosition = () => {
        if (popover.hasAttribute('open')) {
          const buttonRect = button.getBoundingClientRect();
          const popoverRect = popover.getBoundingClientRect();
          
        }
      };
      
      // Watch for popover open/close
      const observer = new MutationObserver(() => {
        updatePopoverPosition();
      });
      
      observer.observe(popover, {
        attributes: true,
        attributeFilter: ['open']
      });
      
      // Update on scroll and resize
      window.addEventListener('scroll', updatePopoverPosition, { passive: true });
      window.addEventListener('resize', updatePopoverPosition, { passive: true });
      
      // Initial update
      updatePopoverPosition();
    }
  
  /**
   * Initialize cart count updates
   */
  initCartCount() {
    const cartBadge = this.header.querySelector('.mt-header__cart-badge');
    const cartDots = this.header.querySelector('[data-cart-dots]');
    
    // Helper function to update cart dots visibility
    const updateCartDotsVisibility = (itemCount) => {
      if (cartDots) {
        cartDots.style.display = itemCount > 0 ? 'flex' : 'none';
      }
    };
    
    // Update cart badge (shows count) if it exists
    if (cartBadge) {
      // Listen for cart updates
      document.addEventListener('cart:updated', (event) => {
        if (event.detail && event.detail.cart) {
          const itemCount = event.detail.cart.item_count || 0;
          cartBadge.textContent = itemCount;
          cartBadge.style.display = itemCount > 0 ? 'flex' : 'none';
          
          // Update dots visibility
          updateCartDotsVisibility(itemCount);
        }
      });

      // Listen for cart refresh
      document.addEventListener('cart:refresh', async () => {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          const itemCount = cart.item_count || 0;
          cartBadge.textContent = itemCount;
          cartBadge.style.display = itemCount > 0 ? 'flex' : 'none';
          
          // Update dots visibility
          updateCartDotsVisibility(itemCount);
        } catch (error) {
          console.error('Error fetching cart:', error);
        }
      });
    } else {
      // Even if cartBadge doesn't exist, still listen for cart updates to update dots
      document.addEventListener('cart:updated', (event) => {
        if (event.detail && event.detail.cart) {
          const itemCount = event.detail.cart.item_count || 0;
          updateCartDotsVisibility(itemCount);
        } else if (event.detail && event.detail.itemCount !== undefined) {
          // Handle alternative event format
          updateCartDotsVisibility(event.detail.itemCount);
        }
      });

      // Listen for cart refresh
      document.addEventListener('cart:refresh', async () => {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          const itemCount = cart.item_count || 0;
          updateCartDotsVisibility(itemCount);
        } catch (error) {
          console.error('Error fetching cart:', error);
        }
      });
    }
    
    // Initialize cart dots visibility by fetching current cart count
    if (cartDots) {
      // Fetch initial cart count
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const initialCount = cart.item_count || 0;
          updateCartDotsVisibility(initialCount);
        })
        .catch(error => {
          console.error('Error fetching initial cart count:', error);
          // Fallback: hide dots if we can't fetch cart
          updateCartDotsVisibility(0);
        });
      
      // Prevent cart-count from updating dots content
      // Override any text content changes to maintain 3 dots
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            // Ensure we always have exactly 3 span elements
            const spans = cartDots.querySelectorAll('span');
            if (spans.length !== 3) {
              cartDots.innerHTML = '<span></span><span></span><span></span>';
            }
          }
        });
      });
      
      observer.observe(cartDots, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
  }

  /**
   * Initialize account notification count
   * For custom notification type, fetch from API or calculate dynamically
   */
  initAccountNotification() {
    const notificationBadge = this.header.querySelector('[data-notification-count]');
    const accountIcon = this.header.querySelector('[data-account-icon]');
    
    if (!notificationBadge || !accountIcon) return;

    // Check if notification type is custom (needs JavaScript)
    const notificationType = accountIcon.closest('.mt-header').dataset?.notificationType || 'unfulfilled_orders';
    
    if (notificationType === 'custom') {
      // Fetch notification count from API or custom logic
      this.fetchAccountNotifications(notificationBadge);
      
      // Listen for notification updates
      document.addEventListener('account:notifications:updated', (event) => {
        if (event.detail && event.detail.count !== undefined) {
          this.updateNotificationBadge(notificationBadge, event.detail.count);
        }
      });
    }
  }

  /**
   * Fetch account notifications from API
   * Customize this method based on your notification source
   */
  async fetchAccountNotifications(badge) {
    try {
      // Example: Fetch from a custom endpoint or Shopify API
      // Replace this with your actual notification API endpoint
      const response = await fetch('/account/notifications/count.json');
      
      if (response.ok) {
        const data = await response.json();
        this.updateNotificationBadge(badge, data.count || 0);
      }
    } catch (error) {
      // If API fails, you can implement fallback logic here
      console.log('Notification API not available, using server-side calculated count');
    }
  }

  /**
   * Update notification badge
   */
  updateNotificationBadge(badge, count) {
    if (count > 0) {
      badge.textContent = count;
      badge.style.display = 'flex';
    } else {
      badge.style.display = 'none';
    }
  }
  
    /**
     * Handle header position based on scroll position
     * When scrollY === 0: position = relative
     * When scrollY !== 0: position = fixed
     */
    handleHeaderPosition() {
      let ticking = false;

      const handleScroll = () => {
        const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
        
        if (scrollY === 0) {
          // At top: position = relative
          this.header.style.position = 'relative';
        } else {
          // Scrolled: position = fixed
          this.header.style.position = 'fixed';
        }
        
        ticking = false;
      };

      // Optimized scroll handler using requestAnimationFrame
      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(handleScroll);
          ticking = true;
        }
      };

      // Add scroll event listener with passive flag for better performance
      window.addEventListener('scroll', onScroll, { passive: true });

      // Initialize: set initial position based on current scroll position
      const initialScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
      if (initialScrollY === 0) {
        this.header.style.position = 'relative';
      } else {
        this.header.style.position = 'fixed';
      }

      // Cleanup function (for theme editor)
      this._positionCleanup = () => {
        window.removeEventListener('scroll', onScroll);
      };
    }

    /**
     * Handle sticky header behavior
     * Hide header when scrolling up (scrollTop increases), show when scrolling down (scrollTop decreases)
     */
    handleStickyHeader() {
      // Check if scroll hide feature is enabled (via data attribute or always enabled)
      const scrollHideEnabled = this.header.dataset.scrollHide !== 'false';
      if (!scrollHideEnabled) return;

      let lastScrollTop = 0;
      let ticking = false;
      
      // Configuration
      const SCROLL_THRESHOLD = 5; // Minimum scroll distance to trigger hide/show
      const TOP_THRESHOLD = 50; // Always show header when near top

      const handleScroll = () => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollDifference = Math.abs(scrollTop - lastScrollTop);

        // If mobile menu is open, always keep header visible and skip hide/show logic
        if (this.isMobileMenuOpen || this.header.dataset.mobileMenuOpen === 'true') {
          this.header.classList.remove('hidden');
          lastScrollTop = scrollTop;
          ticking = false;
          return;
        }

        // Always show header when near top of page
        if (scrollTop <= TOP_THRESHOLD) {
          this.header.classList.remove('hidden');
          lastScrollTop = scrollTop;
          ticking = false;
          return;
        }

        // Only process if scroll difference is significant
        if (scrollDifference < SCROLL_THRESHOLD) {
          ticking = false;
          return;
        }

        // Determine scroll direction and update header immediately
        if (scrollTop > lastScrollTop) {
          // Scrolling down (scrollTop increases) - Hide header
          this.header.classList.add('hidden');
        } else {
          // Scrolling up (scrollTop decreases) - Show header
          this.header.classList.remove('hidden');
        }

        lastScrollTop = scrollTop;
        ticking = false;
      };

      // Optimized scroll handler using requestAnimationFrame
      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(handleScroll);
          ticking = true;
        }
      };

      // Add scroll event listener with passive flag for better performance
      window.addEventListener('scroll', onScroll, { passive: true });

      // Initialize: show header if at top
      const initialScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      lastScrollTop = initialScrollTop;
      
      if (initialScrollTop <= TOP_THRESHOLD) {
        this.header.classList.remove('hidden');
      } else {
        // If already scrolled down, hide header initially
        this.header.classList.add('hidden');
      }

      // Cleanup function (for theme editor)
      this._scrollCleanup = () => {
        window.removeEventListener('scroll', onScroll);
        if (hideTimer) clearTimeout(hideTimer);
        if (showTimer) clearTimeout(showTimer);
      };
    }

    /**
     * Initialize mobile端搜索功能
     */
    initMobileSearch(menu) {
      const searchInput = menu.querySelector('#mt-mobile-search-input');
      const searchForm = menu.querySelector('#mt-mobile-search-form');
      const resultsElement = menu.querySelector('.mt-mobile-menu__search-results');
      const menuListElement = menu.querySelector('.mt-mobile-menu__search-menu-list');

      if (!searchInput) return;

      let abortController = null;
      let searchTimeout = null;

      // 检查是否支持Predictive Search API
      const supportPredictiveApi = () => {
        try {
          const shopifyFeatures = document.getElementById('shopify-features');
          if (shopifyFeatures) {
            const shopifyFeatureRequests = JSON.parse(shopifyFeatures.innerHTML);
            return shopifyFeatureRequests['predictiveSearch'] === true;
          }
        } catch (e) {
          console.error('Error checking predictive search support:', e);
        }
        return false;
      };

      // 执行Predictive Search
      const doPredictiveSearch = async (term) => {
        try {
          const response = await fetch(
            `${window.themeVariables?.routes?.predictiveSearchUrl || '/search/suggest'}?q=${encodeURIComponent(term)}&resources[limit]=10&resources[limit_scope]=each&section_id=predictive-search`,
            {
              signal: abortController.signal,
            }
          );
          const div = document.createElement('div');
          div.innerHTML = await response.text();
          return {
            hasResults: div.querySelector('.predictive-search__results-categories') !== null,
            html: div.firstElementChild?.innerHTML || '',
          };
        } catch (e) {
          if (e.name !== 'AbortError') {
            console.error('Predictive search error:', e);
          }
          return { hasResults: false, html: '' };
        }
      };

      // 执行Liquid Search（兼容模式）
      const doLiquidSearch = async (term) => {
        try {
          const response = await fetch(
            `${window.themeVariables?.routes?.searchUrl || '/search'}?q=${encodeURIComponent(term)}&resources[limit]=50&section_id=predictive-search-compatibility`,
            {
              signal: abortController.signal,
            }
          );
          const div = document.createElement('div');
          div.innerHTML = await response.text();
          return {
            hasResults: div.querySelector('.predictive-search__results-categories') !== null,
            html: div.firstElementChild?.innerHTML || '',
          };
        } catch (e) {
          if (e.name !== 'AbortError') {
            console.error('Liquid search error:', e);
          }
          return { hasResults: false, html: '' };
        }
      };

      // 处理搜索输入
      const handleSearch = async (event) => {
        const term = event.target.value.trim();

        // 取消之前的搜索
        if (abortController) {
          abortController.abort();
        }
        if (searchTimeout) {
          clearTimeout(searchTimeout);
        }

        if (term === '') {
          // 空输入时显示菜单
          if (resultsElement) resultsElement.hidden = true;
          if (menuListElement) menuListElement.hidden = false;
          return;
        }

        // 隐藏菜单，准备显示结果
        if (resultsElement) resultsElement.hidden = true;
        if (menuListElement) menuListElement.hidden = true;

        // 防抖处理
        searchTimeout = setTimeout(async () => {
          abortController = new AbortController();
          let searchResults = {};

          try {
            if (supportPredictiveApi()) {
              searchResults = await doPredictiveSearch(term);
            } else {
              searchResults = await doLiquidSearch(term);
            }
          } catch (e) {
            if (e.name !== 'AbortError') {
              console.error('Search error:', e);
            }
            return;
          }

          // 显示结果
          if (resultsElement) {
            resultsElement.hidden = false;
            resultsElement.innerHTML = searchResults.html || '<p>No results found</p>';
          }
        }, 300);
      };

      // 搜索输入事件
      searchInput.addEventListener('input', handleSearch);

      // 表单提交
      if (searchForm) {
        searchForm.addEventListener('submit', (e) => {
          const term = searchInput.value.trim();
          if (term === '') {
            e.preventDefault();
          }
        });
      }
    }

    /**
     * Initialize PC端搜索框
     */
    initSearchBox() {
      // 只在PC端初始化
      if (window.matchMedia('(max-width: 989px)').matches) {
        return;
      }

      const searchToggle = this.header.querySelector('[data-mt-search-toggle]');
      const searchBox = document.querySelector('#mt-search-box');
      const searchInput = document.querySelector('#mt-search-input');
      const searchClose = document.querySelector('[data-mt-search-close]');
      const searchForm = document.querySelector('#mt-search-form');
      const resultsElement = searchBox?.querySelector('.mt-search-box__results');
      const loadingElement = searchBox?.querySelector('.mt-search-box__loading');
      const menuListElement = searchBox?.querySelector('.mt-search-box__menu-list');

      if (!searchToggle || !searchBox || !searchInput) return;

      let abortController = null;
      let searchTimeout = null;

      // 锁定body滚动
      const lockBodyScroll = () => {
        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.setAttribute('data-scroll-lock', scrollY.toString());
      };

      // 解锁body滚动
      const unlockBodyScroll = () => {
        const scrollY = document.body.getAttribute('data-scroll-lock') || '0';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.removeAttribute('data-scroll-lock');
        window.scrollTo(0, parseInt(scrollY, 10));
      };

      // 打开搜索框
      const openSearchBox = () => {
        searchBox.hidden = false;
        searchBox.setAttribute('data-open', 'true');
        searchToggle.setAttribute('aria-expanded', 'true');
        
        // 锁定页面滚动
        lockBodyScroll();
        
        // 聚焦输入框
        requestAnimationFrame(() => {
          searchInput.focus();
        });
      };

      // 关闭搜索框
      const closeSearchBox = () => {
        searchBox.setAttribute('data-open', 'false');
        searchToggle.setAttribute('aria-expanded', 'false');
        
        // 解锁页面滚动
        unlockBodyScroll();
        
        // 清空输入和结果
        searchInput.value = '';
        if (resultsElement) {
          resultsElement.hidden = true;
          resultsElement.innerHTML = '';
        }
        if (loadingElement) {
          loadingElement.hidden = true;
        }
        if (menuListElement) {
          menuListElement.hidden = false;
        }

        // 延迟隐藏以完成动画
        setTimeout(() => {
          if (searchBox.getAttribute('data-open') === 'false') {
            searchBox.hidden = true;
          }
        }, 300);

        // 取消正在进行的搜索
        if (abortController) {
          abortController.abort();
        }
        if (searchTimeout) {
          clearTimeout(searchTimeout);
        }
      };

      // 检查是否支持Predictive Search API
      const supportPredictiveApi = () => {
        try {
          const shopifyFeatures = document.getElementById('shopify-features');
          if (shopifyFeatures) {
            const shopifyFeatureRequests = JSON.parse(shopifyFeatures.innerHTML);
            return shopifyFeatureRequests['predictiveSearch'] === true;
          }
        } catch (e) {
          console.error('Error checking predictive search support:', e);
        }
        return false;
      };

      // 执行Predictive Search
      const doPredictiveSearch = async (term) => {
        try {
          const response = await fetch(
            `${window.themeVariables?.routes?.predictiveSearchUrl || '/search/suggest'}?q=${encodeURIComponent(term)}&resources[limit]=10&resources[limit_scope]=each&section_id=predictive-search`,
            {
              signal: abortController.signal,
            }
          );
          const div = document.createElement('div');
          div.innerHTML = await response.text();
          return {
            hasResults: div.querySelector('.predictive-search__results-categories') !== null,
            html: div.firstElementChild?.innerHTML || '',
          };
        } catch (e) {
          if (e.name !== 'AbortError') {
            console.error('Predictive search error:', e);
          }
          return { hasResults: false, html: '' };
        }
      };

      // 执行Liquid Search（兼容模式）
      const doLiquidSearch = async (term) => {
        try {
          const response = await fetch(
            `${window.themeVariables?.routes?.searchUrl || '/search'}?q=${encodeURIComponent(term)}&resources[limit]=50&section_id=predictive-search-compatibility`,
            {
              signal: abortController.signal,
            }
          );
          const div = document.createElement('div');
          div.innerHTML = await response.text();
          return {
            hasResults: div.querySelector('.predictive-search__results-categories') !== null,
            html: div.firstElementChild?.innerHTML || '',
          };
        } catch (e) {
          if (e.name !== 'AbortError') {
            console.error('Liquid search error:', e);
          }
          return { hasResults: false, html: '' };
        }
      };

      // 处理搜索输入
      const handleSearch = async (event) => {
        const term = event.target.value.trim();

        // 取消之前的搜索
        if (abortController) {
          abortController.abort();
        }
        if (searchTimeout) {
          clearTimeout(searchTimeout);
        }

        if (term === '') {
          // 空输入时显示菜单
          if (loadingElement) loadingElement.hidden = true;
          if (resultsElement) resultsElement.hidden = true;
          if (menuListElement) menuListElement.hidden = false;
          return;
        }

        // 显示加载状态
        if (loadingElement) loadingElement.hidden = false;
        if (resultsElement) resultsElement.hidden = true;
        if (menuListElement) menuListElement.hidden = true;

        // 防抖处理
        searchTimeout = setTimeout(async () => {
          abortController = new AbortController();
          let searchResults = {};

          try {
            if (supportPredictiveApi()) {
              searchResults = await doPredictiveSearch(term);
            } else {
              searchResults = await doLiquidSearch(term);
            }
          } catch (e) {
            if (e.name !== 'AbortError') {
              console.error('Search error:', e);
            }
            return;
          }

          // 显示结果
          if (loadingElement) loadingElement.hidden = true;
          if (resultsElement) {
            resultsElement.hidden = false;
            resultsElement.innerHTML = searchResults.html || '<p>No results found</p>';
          }
        }, 300);
      };

      // 绑定事件
      searchToggle.addEventListener('click', (e) => {
        e.preventDefault();
        if (searchBox.getAttribute('data-open') === 'true') {
          closeSearchBox();
        } else {
          openSearchBox();
        }
      });

      if (searchClose) {
        searchClose.addEventListener('click', (e) => {
          e.preventDefault();
          closeSearchBox();
        });
      }

      // ESC键关闭
      const handleEscapeKey = (e) => {
        if (e.key === 'Escape' && searchBox.getAttribute('data-open') === 'true') {
          closeSearchBox();
        }
      };
      document.addEventListener('keydown', handleEscapeKey);

      // 点击外部区域关闭搜索框
      const handleClickOutside = (e) => {
        if (searchBox.getAttribute('data-open') === 'true') {
          // 检查点击是否在搜索框外部
          const isClickInsideSearchBox = searchBox.contains(e.target);
          const isClickOnToggle = searchToggle.contains(e.target);
          
          if (!isClickInsideSearchBox && !isClickOnToggle) {
            closeSearchBox();
          }
        }
      };
      document.addEventListener('click', handleClickOutside);

      // 搜索输入事件
      searchInput.addEventListener('input', handleSearch);

      // 表单提交
      if (searchForm) {
        searchForm.addEventListener('submit', (e) => {
          const term = searchInput.value.trim();
          if (term === '') {
            e.preventDefault();
          }
        });
      }
    }
  }
  
  // Initialize when DOM is ready
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const headers = document.querySelectorAll('.mt-header');
      headers.forEach(header => {
        new MTHeader(header);
      });
    });
  } else {
    const headers = document.querySelectorAll('.mt-header');
    headers.forEach(header => {
      new MTHeader(header);
    });
  }
  
  // Re-initialize when sections are loaded (for theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const header = event.target.querySelector('.mt-header');
      if (header) {
        new MTHeader(header);
      }
    });
  }
  
  